import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Button, Card, Segmented, Space, Spin, Table, Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { REGION_LABELS } from './constants';
import useSegmentationRunsQuery from './RunsList/use-segmentation-runs-query';
import useSegmentationRunQuery from './RunDetail/use-segmentation-run-query';
import DistributionSummary from './DistributionSummary';
import RepUpdateCopy from './RepUpdateCopy';

const { Text, Title } = Typography;

const REGION_SEGMENTS = [
  { label: 'NZ', value: 'NZ' },
  { label: 'AU', value: 'AU' },
];

const STAFF_RUN_LIMIT = 50;

const runReference = (run) => run?.summaryJson?.scoring?.referenceDate || run?.createdAt;

const formatRunMonth = (run) => {
  const ref = runReference(run);
  if (!ref || !dayjs(ref).isValid()) return '—';
  return dayjs(ref).format('MMMM YYYY');
};

const runTime = (run) => {
  const ref = runReference(run);
  if (!ref) return 0;
  const parsed = dayjs(ref);
  return parsed.isValid() ? parsed.valueOf() : 0;
};

const StaffRunDetail = ({ runId, onBack }) => {
  const { data: run, isLoading } = useSegmentationRunQuery(runId);

  if (isLoading && !run) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (!run) {
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} style={{ marginBottom: 16 }}>
          Back to runs
        </Button>
        <Alert type="error" showIcon message="Run not found." />
      </div>
    );
  }

  const regionLabel = REGION_LABELS[run.region] ?? run.region ?? '';
  const month = formatRunMonth(run);
  const heading = [regionLabel, month].filter(Boolean).join(' — ');

  return (
    <div>
      <div className="segmentation-detail-header">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            Back
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {heading}
          </Title>
        </Space>
      </div>
      <Card className="segmentation-section" title="Segment distribution">
        <DistributionSummary summary={run.summaryJson} />
      </Card>
      <RepUpdateCopy run={run} formatted />
    </div>
  );
};

StaffRunDetail.propTypes = {
  runId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onBack: PropTypes.func.isRequired,
};

const StaffView = () => {
  const [region, setRegion] = useState('NZ');
  const [selectedRunId, setSelectedRunId] = useState(null);
  const { data: runs, isLoading } = useSegmentationRunsQuery({ limit: STAFF_RUN_LIMIT });

  const rows = useMemo(() => {
    const list = Array.isArray(runs) ? runs : [];
    return list
      .filter((run) => run.status === 'completed' && run.region === region)
      .sort((a, b) => runTime(b) - runTime(a))
      .map((run) => ({
        ...run,
        key: run.id,
        month: formatRunMonth(run),
      }));
  }, [runs, region]);

  if (selectedRunId != null) {
    return (
      <StaffRunDetail
        runId={selectedRunId}
        onBack={() => setSelectedRunId(null)}
      />
    );
  }

  return (
    <>
      <Segmented
        className="segmentation-region-toggle"
        options={REGION_SEGMENTS}
        value={region}
        onChange={setRegion}
      />
      <Card className="segmentation-section" title="Completed runs">
        <Table
          size="middle"
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          onRow={(record) => ({
            onClick: () => setSelectedRunId(record.id),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: `No completed ${REGION_LABELS[region] ?? region} runs yet.`,
          }}
          pagination={{ pageSize: 12, hideOnSinglePage: true }}
          columns={[
            {
              title: 'Month',
              dataIndex: 'month',
              key: 'month',
              render: (month) => <Text strong>{month}</Text>,
            },
            {
              title: '',
              key: 'action',
              align: 'right',
              render: (_, run) => (
                <Button
                  type="link"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedRunId(run.id);
                  }}
                >
                  View
                </Button>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
};

export default StaffView;
